import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Platform,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store";
import LHConfirmationDialog from "../../../components/LHConfirmationDialog";
import {
  resetFilters,
  resetWalkId,
} from "../../../store/slices/leaderShipWalkSlice";
import { selectWalkState } from "../../../store/slices/leadershipWalkRedirectSlice";
import DLOBreadcrumb, {
  DLOBreadcrumbItem,
} from "../../../modules/dlo/components/DLOBreadcrumb";
import FontFamily from "../../../constants/fonts";
import { useTheme } from "../../../theme";

interface LeadershipWalkHeaderProps {
  onReturn: () => void;
  onHomePress?: () => void; // Add optional home navigation handler
  title?: string;
  subtitle?: string;
  logoSource?: any; // Optional logo source for future use
  status?: string;
}

const { width: screenWidth } = Dimensions.get("window");

const styles = StyleSheet.create({
  header: {
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    paddingHorizontal: screenWidth < 600 ? 16 : 0,
    paddingTop: screenWidth < 600 ? 16 : 16,
    paddingBottom: 8,
    borderBottomColor: "#e9ecef",
  },
  leftGroup: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    marginTop: 8,
  },
  breadcrumbContainer: {
    marginBottom: 12,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "hidden",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: screenWidth < 600 ? 6 : 8,
    paddingHorizontal: 0,
    marginRight: 12,
  },
  backIcon: {
    fontSize: screenWidth < 600 ? 18 : 20,
    marginRight: 8,
    color: "#005CBC",
  },
  backButtonText: {
    fontFamily: FontFamily.regular,
    fontSize: screenWidth < 600 ? 14 : 16,
    color: "#005CBC",
    fontWeight: "500",
  },
  logo: {
    width: screenWidth < 600 ? 50 : 80,
    height: screenWidth < 600 ? 30 : 40,
    marginRight: 12,
  },
  textGroup: {
    flexShrink: 1,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    fontStyle: "normal",
    fontSize: screenWidth < 600 ? 20 : 28,
    lineHeight: screenWidth < 600 ? 26 : 40,
    letterSpacing: 0,
    color:
      Platform.OS === "web" ? "var(--Tokens-Text-Primary, #2D2D2D)" : "#2D2D2D",
  },
  subtitle: {
    fontFamily: FontFamily.regular,
    fontWeight: "400",
    fontStyle: "normal",
    fontSize: screenWidth < 600 ? 12 : 16,
    lineHeight: screenWidth < 600 ? 16 : 22,
    letterSpacing: 0,
    color: "#495057",
    marginTop: 2,
  },
  button: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0066CC",
    paddingHorizontal: screenWidth < 600 ? 12 : 16,
    paddingVertical: screenWidth < 600 ? 8 : 10,
    borderRadius: 6,
    flexShrink: 0,
  },
  buttonIcon: {
    color: "white",
    fontSize: screenWidth < 600 ? 14 : 16,
    marginRight: 4,
  },
  buttonText: {
    color: "white",
    fontSize: screenWidth < 600 ? 12 : 14,
    fontWeight: "500",
  },
  instructionBanner: {
    backgroundColor: "#CCEAF6",
    color: "black",
    textAlign: "left",
    padding: screenWidth < 600 ? 8 : 12,
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "center",
    gap: screenWidth < 600 ? 6 : 8,
    width: "100%",
    maxWidth: "100%",
    alignSelf: "center",
    height: screenWidth < 600 ? 48 : 56,
    justifyContent: "flex-start",
    paddingVertical: screenWidth < 600 ? 8 : 12,
    paddingHorizontal: screenWidth < 600 ? 12 : 16,
    marginTop: 8,
    marginBottom: -24,
  },
  infoIconCircle: {
    width: screenWidth < 600 ? 20 : 20,
    height: screenWidth < 600 ? 20 : 20,
    borderRadius: screenWidth < 600 ? 12 : 14,
    backgroundColor: "transparent",
    justifyContent: "center",
    alignItems: "center",
    marginRight: screenWidth < 600 ? 8 : 12,
    borderWidth: 2,
    borderColor:
      Platform.OS === "web"
        ? "var(--Tokens-Feedback-Info, #0078A7)"
        : "#0078A7",
  },
  infoIconText: {
    color:
      Platform.OS === "web"
        ? "var(--Tokens-Feedback-Info, #0078A7)"
        : "#0078A7",
    fontWeight: "700",
    fontSize: screenWidth < 600 ? 12 : 14,
  },
  instructionText: {
    fontFamily: FontFamily.regular,
    fontWeight: "400",
    fontStyle: "normal",
    fontSize: screenWidth < 600 ? 11 : 14,
    lineHeight: screenWidth < 600 ? 14 : 20,
    letterSpacing: 0,
    color: "#0B0B0B",
    flex: 1,
  },
  breadcrumbStyle: {
    flexDirection: "row",
    alignItems: "center",
  },
});

const LeadershipWalkHeader: React.FC<LeadershipWalkHeaderProps> = ({
  onReturn,
  onHomePress,
  title = "Weekly Leadership Walk",
  subtitle: propSubtitle,
  status = "in-progress",
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const titleColor = isDark ? colors.text.inverse : '#2D2D2D';
  const subtitleColor = isDark ? colors.text.inverse : '#495057';
  const bannerBg = isDark ? colors.background.surface : '#CCEAF6';

  const dispatch = useDispatch();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState<
    "home" | "leadershipwalk"
  >("leadershipwalk");
  const [initialData, setInitialData] = useState<any>(null);
  const [hasSessionChanges, setHasSessionChanges] = useState(false);
  const [isDataStabilized, setIsDataStabilized] = useState(false);

  // Get selected zone from Redux store
  const { selectedZone } = useSelector(
    (state: RootState) => state.leadershipWalk
  );

  // Get the walk state from Redux store to determine if the walk is completed
  const walkState = useSelector(selectWalkState);

  // Get loading state to determine when data is fully loaded
  const loading = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.loading
  );

  // Get leadership walk data from Redux to check for changes
  const selectedOptions = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.selectedOptions
  );
  const managerSignatureFileName = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.managerSignatureFileName
  );
  const managerSignatureImageUrl = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.managerSignatureImageUrl
  );
  const miscellaneousObservations = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.miscellaneousObservations
  );
  const miscObservationImages = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.miscObservationImages
  );

  // Get the selected questions to detect when a new walk is loaded
  const selectedQuestions = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.selectedQuestions
  );

  // Memoize current data to prevent unnecessary re-calculations
  const currentData = useMemo(
    () => ({
      selectedOptions,
      managerSignatureFileName,
      managerSignatureImageUrl,
      miscellaneousObservations,
      miscObservationImages,
    }),
    [
      selectedOptions,
      managerSignatureFileName,
      managerSignatureImageUrl,
      miscellaneousObservations,
      miscObservationImages,
    ]
  );

  // Reset change tracking when a new walk is loaded
  useEffect(() => {
    // Reset initial data and change tracking when selectedQuestions changes (new walk loaded)
    setInitialData(null);
    setIsDataStabilized(false);
    setHasSessionChanges(false);
  }, [selectedQuestions]);

  // Capture initial data after loading is complete and data is stable
  useEffect(() => {
    if (!initialData && !loading) {
      // Delay initial data capture to ensure all data is loaded and stable
      const timer = setTimeout(() => {
        setInitialData(JSON.parse(JSON.stringify(currentData)));
        setIsDataStabilized(true);
      }, 1000); // 1 second delay to allow for any remaining async operations

      return () => clearTimeout(timer);
    }
  }, [currentData, initialData, loading]);

  // Track changes by comparing current data with initial data, but only after data is stabilized
  useEffect(() => {
    if (initialData && isDataStabilized) {
      // Only track changes if we have meaningful data (not just empty objects)
      const hasSignature = !!currentData.managerSignatureFileName;
      const hasObservations =
        !!currentData.miscellaneousObservations &&
        currentData.miscellaneousObservations.trim() !== "";
      const hasOptions =
        currentData.selectedOptions && currentData.selectedOptions.length > 0;
      const hasImages =
        currentData.miscObservationImages &&
        currentData.miscObservationImages.length > 0;

      // Only consider it a change if there's actual user-inputted data
      const hasActualData =
        hasSignature || hasObservations || hasOptions || hasImages;

      if (hasActualData) {
        const hasChanges =
          JSON.stringify(currentData) !== JSON.stringify(initialData);
        setHasSessionChanges(hasChanges);
      } else {
        // If no actual data, don't consider it as changes
        setHasSessionChanges(false);
      }
    }
  }, [currentData, initialData, isDataStabilized]);

  // Function to check if there are unsaved changes made during this session
  const hasUnsavedChanges = (): boolean => {
    // Only consider it as changes if there's actual user-inputted data
    const hasSignature = !!managerSignatureFileName;
    const hasObservations =
      !!miscellaneousObservations && miscellaneousObservations.trim() !== "";
    const hasOptions = selectedOptions && selectedOptions.length > 0;
    const hasImages = miscObservationImages && miscObservationImages.length > 0;

    // If there's no meaningful data, don't show confirmation
    if (!hasSignature && !hasObservations && !hasOptions && !hasImages) {
      return false;
    }

    // Only return true if data is stabilized and changes have been made during this session
    return isDataStabilized && hasSessionChanges;
  };

  // Create subtitle from zone data if not provided as prop
  const subtitle =
    propSubtitle ||
    (selectedZone
      ? `Zones: ${selectedZone.map((zone) => zone.ZONE).join(", ")}`
      : "");

  // Handle Back click to Leadership Walk dashboard
  const handleReturnClick = () => {
    if (walkState === "completed") {
      dispatch(resetFilters());
      dispatch(resetWalkId());
      if (onReturn) {
        onReturn();
      }
      return;
    }
    // Show confirmation only if there are unsaved changes and walk is not completed
    if (
      (status === "in-progress" || status === "not_started") &&
      hasUnsavedChanges()
    ) {
      setNavigationTarget("leadershipwalk");
      setShowConfirmDialog(true);
    } else {
      // If no changes or completed status, just go back immediately
      dispatch(resetFilters());
      dispatch(resetWalkId());
      if (onReturn) {
        onReturn();
      }
    }
  };

  // Handle Home navigation
  const handleHomeClick = () => {
    if (walkState === "completed") {
      dispatch(resetFilters());
      dispatch(resetWalkId());
      if (onHomePress) {
        onHomePress();
      }
      return;
    }
    // Show confirmation only if there are unsaved changes and walk is not completed
    if (
      (status === "in-progress" || status === "not_started") &&
      hasUnsavedChanges()
    ) {
      setNavigationTarget("home");
      setShowConfirmDialog(true);
    } else {
      // If no changes or completed status, just navigate immediately
      dispatch(resetFilters());
      dispatch(resetWalkId());
      if (onHomePress) {
        onHomePress();
      }
    }
  };

  const handleConfirm = () => {
    dispatch(resetFilters());
    dispatch(resetWalkId());
    setShowConfirmDialog(false);

    // Reset session changes flag
    setHasSessionChanges(false);

    if (navigationTarget === "home" && onHomePress) {
      onHomePress();
    } else if (navigationTarget === "leadershipwalk" && onReturn) {
      onReturn();
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  // Create breadcrumb items (matching PulseCheckHeader structure)
  const breadcrumbItems: DLOBreadcrumbItem[] = [
    {
      id: "leadershipwalk",
      label: "Leadership Walk",
      onPress: handleReturnClick,
    },
    {
      id: "current",
      label:
        selectedZone && selectedZone.length > 0
          ? selectedZone.map((zone) => zone.ZONE).join(", ")
          : "Walk Details",
      isCurrentPage: true,
    },
  ];

  return (
    <>
      <View style={[styles.header, isDark && { borderBottomColor: colors.border.default }] }>
        {/* Breadcrumb Navigation */}
        <View style={styles.breadcrumbContainer}>
          {screenWidth < 600 ? (
            <TouchableOpacity
              onPress={handleReturnClick}
              style={styles.backButton}
              accessibilityRole="button"
              accessibilityLabel="Back to Leadership Walk"
            >
              <Text style={[styles.backIcon, isDark && { color: colors.text.inverse }]}>←</Text>
              <Text style={[styles.backButtonText, isDark && { color: colors.text.inverse }]}>Back to Leadership Walk</Text>
            </TouchableOpacity>
          ) : (
            <DLOBreadcrumb
              items={breadcrumbItems}
              showHomeIcon={true}
              onHomePress={() => handleHomeClick()}
              style={styles.breadcrumbStyle}
            />
          )}
        </View>

        {/* Title and subtitle */}
        <View style={styles.leftGroup}>
          <View style={styles.textGroup}>
            <Text style={[styles.title, { color: titleColor }]}>{title}</Text>
            <Text style={[styles.subtitle, { color: subtitleColor }]}>{subtitle}</Text>
          </View>
        </View>
      </View>

      {(walkState === "not_started" || walkState === "in_progress") && (
        <View
          style={[
            styles.instructionBanner,
            {
              backgroundColor: bannerBg,
              borderColor: isDark ? colors.border.default : 'transparent',
              borderWidth: isDark ? 1 : 0,
            },
          ]}
        >
          <View
            style={[
              styles.infoIconCircle,
              { borderColor: isDark ? colors.text.inverse : '#0078A7' },
            ]}
          >
            <Text style={[styles.infoIconText, { color: isDark ? colors.text.inverse : '#0078A7' }]}>i</Text>
          </View>
          <Text style={[styles.instructionText, { color: isDark ? colors.text.inverse : '#0B0B0B' }]}>
            Please select any behaviors or observations you see below
          </Text>
        </View>
      )}

      <LHConfirmationDialog
        visible={showConfirmDialog}
        message="You have unsaved data, are you sure you want to go back?"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default LeadershipWalkHeader;
